import React, { useState, useEffect, useRef } from 'react';
import pt from 'prop-types';
import isEqualWith from 'lodash/isEqualWith';
import {
  resolveThemeSettings,
  getThemeSettingsFromProps,
} from '@swell/apps-sdk';

import { InlineEditableProvider } from 'components/storefront/editor/easyblocks-page/components/inline-editable';
import { injectGlobalCSS } from 'components/storefront/editor/global-css-widget';

import { RootContext } from './index';
import parse from './parser';

/** @typedef {import('@swell/apps-sdk').SwellTheme} SwellTheme */
/** @typedef {import('components/storefront/editor/easyblocks-page/asset-loader').default} AssetLoader */

/**
 * @typedef {object} RootProps
 * @property {SwellTheme} theme
 * @property {AssetLoader} assetLoader
 */

function settingsCompareFunc(first, second) {
  // compare _id for storefront resources
  if (first?._getter && second?._getter) {
    return first._id === second._id;
  }

  // return undefined to use standard comparison
}

/** @param {RootProps} props */
function Root(props) {
  const {
    Root,
    pageProps,
    layoutProps,
    pageContent,
    editorSchema,
    assetLoader,
    theme,
  } = props;

  const [template, setTemplate] = useState('');
  const [pageContentString, setPageContentString] = useState(null);
  const [Content, setContent] = useState(() => <div />);
  const settingsRef = useRef(null);

  useEffect(() => {
    const settingProps = Object.assign(
      {},
      props.pageProps.settings || {},
      getThemeSettingsFromProps(props, editorSchema),
    );

    const settings = resolveThemeSettings(theme, settingProps, editorSchema);

    if (isEqualWith(settingsRef.current, settings, settingsCompareFunc)) {
      return;
    }

    theme.setGlobals({ settings });
    settingsRef.current = settings;
    // force settings apply
    window.setThemeGlobals(theme.globals);
  }, [props, editorSchema, theme]);

  useEffect(() => {
    console.log('render layout', { settings: theme.globals?.settings });

    if (theme.globals?.settings?.custom_css) {
      let customCss = theme.globals.settings.custom_css;
      if (Array.isArray(customCss)) {
        customCss = customCss.join('\n');
      }

      injectGlobalCSS(customCss);
    }

    let canceled = false;

    theme
      .renderLayout(
        pageContent.layout || '',
        layoutProps,
        '<content-for-layout></content-for-layout>',
        theme.getContentForHeader(),
      )
      .then(async (template) => {
        if (canceled) return;

        // Render theme page directly when it's a raw liquid file
        const pageContentString =
          typeof pageContent === 'string'
            ? await theme.renderTemplateString(pageContent, pageProps)
            : null;

        if (canceled) return;

        setTemplate(template);
        setPageContentString(pageContentString);
      });

    return () => {
      canceled = true;
    };
  }, [layoutProps, pageContent, pageProps, theme, theme.globals]);

  useEffect(() => {
    if (!template) {
      return;
    }

    const { Content } = parse(theme, assetLoader, template, pageContentString);

    setContent(Content);
  }, [template, pageContentString, theme, assetLoader]);

  const RootComponent = Root.type;

  return (
    <RootComponent {...Root.props} className="easyblocks-page--root">
      <RootContext.Provider value={props}>
        <InlineEditableProvider theme={theme}>{Content}</InlineEditableProvider>
      </RootContext.Provider>
    </RootComponent>
  );
}

Root.propTypes = {
  Root: pt.element,
  theme: pt.object,
  pageProps: pt.object,
  layoutProps: pt.object,
  pageContent: pt.oneOfType([pt.string, pt.object]),
  editorSchema: pt.array,
  assetLoader: pt.object,
};

function getRootComponent(props, theme, assetLoader) {
  const { layoutProps, pageProps, pageContent } = props;

  const editorSchema = pageProps.configs?.editor?.settings;

  function RootWrapper(rootProps) {
    return (
      <Root
        layoutProps={layoutProps}
        pageProps={pageProps}
        pageContent={pageContent}
        editorSchema={editorSchema}
        assetLoader={assetLoader}
        theme={theme}
        {...rootProps}
      />
    );
  }

  return React.memo(RootWrapper);
}

export default getRootComponent;
